// Adapters for Guidance and Process Maps data transformation
// Handles transformation between API DTOs and UI models
// Fail loudly on contract violations - no silent data corruption

import {
  GuidanceDto,
  ProcessMapDto,
  ApiResponse,
  PaginatedResponse
} from './guidanceAndProcessMaps.dto';

// UI Models (what the components expect)
export interface Guidance {
  id: number;
  title: string;
  description: string;
  content: string;
  category: string;
  framework_id: number | null;
  framework_name: string | null;
  scope_id: number | null;
  scope_name: string | null;
  section_id: number | null;
  section_name: string | null;
  page_id: number | null;
  page_name: string | null;
  route_path: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProcessMap {
  id: number;
  title: string;
  description: string;
  diagram_data: string;
  category: string;
  framework_id: number | null;
  framework_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Adapter functions - transform DTOs to UI models
export class GuidanceAndProcessMapsAdapter {
  static adaptGuidance(dto: GuidanceDto): Guidance {
    if (!dto || typeof dto.id !== 'number') {
      throw new Error('Invalid Guidance DTO: missing or invalid id');
    }

    return {
      id: dto.id,
      title: dto.title || '',
      description: dto.description || '',
      content: dto.content || '',
      category: dto.category || '',
      framework_id: dto.framework_id ?? null,
      framework_name: dto.framework_name ?? null,
      scope_id: dto.scope_id ?? null,
      scope_name: dto.scope_name ?? null,
      section_id: dto.section_id ?? null,
      section_name: dto.section_name ?? null,
      page_id: dto.page_id ?? null,
      page_name: dto.page_name ?? null,
      route_path: dto.route_path ?? null,
      is_active: Boolean(dto.is_active),
      created_at: dto.created_at || '',
      updated_at: dto.updated_at || '',
    };
  }

  static adaptProcessMap(dto: ProcessMapDto): ProcessMap {
    if (!dto || typeof dto.id !== 'number') {
      throw new Error('Invalid ProcessMap DTO: missing or invalid id');
    }

    return {
      id: dto.id,
      title: dto.title || '',
      description: dto.description || '',
      diagram_data: dto.diagram_data || '',
      category: dto.category || '',
      framework_id: dto.framework_id ?? null,
      framework_name: dto.framework_name ?? null,
      is_active: Boolean(dto.is_active),
      created_at: dto.created_at || '',
      updated_at: dto.updated_at || '',
    };
  }

  // Array adapters
  static adaptGuidances(dtos: GuidanceDto[]): Guidance[] {
    if (!Array.isArray(dtos)) {
      throw new Error('Invalid Guidance DTOs: expected array');
    }
    return dtos.map(dto => this.adaptGuidance(dto));
  }

  static adaptProcessMaps(dtos: ProcessMapDto[]): ProcessMap[] {
    if (!Array.isArray(dtos)) {
      throw new Error('Invalid ProcessMap DTOs: expected array');
    }
    return dtos.map(dto => this.adaptProcessMap(dto));
  }

  // API Response adapters
  static adaptApiResponse<T, U>(
    response: ApiResponse<T>,
    adapter: (data: T) => U
  ): U {
    if (!response || response.success !== true) {
      throw new Error(`API response indicates failure: ${(response as any)?.message || 'Unknown error'}`);
    }
    if (!response.data) {
      throw new Error('API response missing data');
    }
    return adapter(response.data);
  }

  static adaptPaginatedResponse<T, U>(
    response: PaginatedResponse<T>,
    adapter: (data: T[]) => U[]
  ): { data: U[]; pagination: PaginatedResponse<T>['pagination'] } {
    if (!response || response.success !== true) {
      throw new Error(`API response indicates failure: ${(response as any)?.message || 'Unknown error'}`);
    }
    if (!Array.isArray(response.data)) {
      throw new Error('API paginated response data is not an array');
    }
    return {
      data: adapter(response.data),
      pagination: response.pagination
    };
  }
}